import { useParams } from 'next/navigation';
import Link from 'next/link';
import dayjs, { parseDate } from 'utils/dayjs.config';

import { ConversationWithLastMessage } from 'api/conversation/types';
import { Bot } from 'api/bot/types';
import { useChatStore } from 'store/chat';
import { useMemo, useState } from 'react';
import { cutString } from 'utils';
import useWindowWidth from 'hooks/useWindowWidth';
import { ThreeDots } from 'components/icons/ThreeDots';

import { useOutsideClick } from 'hooks/useOutsideClick';
import { useMutation } from 'react-query';
import { postConversationAction } from 'api/conversation';
import { showToast } from 'components/ui/CustomToast';
import { reactQuery } from 'lib/reactQuery';
import { useRouter } from 'next/router';
import { useAuthStore } from 'store/auth';
import { ConfirmDeleteModal } from 'components/modals/ConfirmDeleteModal';

type Props = {
  bot?: Bot;
  item?: ConversationWithLastMessage;
  mostRecentConversation?: ConversationWithLastMessage;
};

const formatTimeDifference = (givenDate: dayjs.Dayjs) => {
  const now = dayjs();

  const diffInMinutes = now.diff(givenDate, 'minute');
  const diffInHours = now.diff(givenDate, 'hour');
  const diffInDays = now.diff(givenDate, 'day');

  if (Math.abs(diffInMinutes) < 60) {
    if (diffInMinutes === 0) return '1m ago';
    return `${Math.abs(diffInMinutes)}m ago`;
  } else if (Math.abs(diffInHours) < 24) {
    return `${Math.abs(diffInHours)}h`;
  } else {
    return `${Math.abs(diffInDays)}d`;
  }
};

export const ConversationItem = ({ item, bot, mostRecentConversation }: Props) => {
  const { setIsVisibleConversations } = useChatStore();
  const params = useParams();
  const { chats, removeChat } = useChatStore();
  const { push } = useRouter();
  const { isLogged } = useAuthStore();

  const [isOpenActions, setIsOpenActions] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

  const width = useWindowWidth();

  const paramsBotName = params?.bot_name as string;
  const bot_name = item?.bot_name || bot?.bot_name;
  const bot_id = item?.account_id || bot?.id;
  const attributes = item?.bot_attributes || bot?.custom_attributes;
  const botAvatar = attributes?.avatar_url || attributes?.profile_image_url;

  const isUnread = useMemo(
    () => Boolean(chats.find((chat) => chat.account_id === item?.account_id && chat.unread)),
    [chats, item]
  );

  const { ref } = useOutsideClick(() => {
    if (isOpenActions) setIsOpenActions(false);
  });

  const { mutate, isLoading } = useMutation(postConversationAction);

  const onSuccess = (message: string) => {
    setIsOpenActions(false);
    showToast({ type: 'success', message });
  };

  return (
    <div
      className={`relative flex items-center gap-2 pr-4 cursor-pointer border-t border-primary justify-between ${
        bot_name === paramsBotName ? 'bg-stone-600' : 'hover:bg-stone-800'
      }`}
      ref={ref}
    >
      <Link
        prefetch={false}
        href={`/chat/${bot_id}/${bot_name}`}
        className="flex items-center gap-2 w-full py-3 px-4"
        onClick={() => {
          if (width <= 1024) {
            setTimeout(() => setIsVisibleConversations(false), 300);
          }
        }}
      >
        <div className="w-12 h-12 relative rounded-full">
          {attributes && (
            <img src={botAvatar} alt="AppName" className="w-full h-full rounded-full object-cover object-top" />
          )}
        </div>
        <div className="flex-1 flex flex-col gap-0.5">
          <p className="text-sm font-semibold">{attributes?.full_name}</p>
          {item?.last_message && (
            <div className="text-xs text-white text-opacity-30">
              <span className={`break-all ${isUnread ? 'text-white font-semibold' : ''}`}>
                {(cutString(item.last_message.content.replace(/<[^>]*>/g, ''), 30).trim() || '<Photo>')}
              </span>
              <span className="mx-1.5">·</span>
              <span>{formatTimeDifference(parseDate(item.last_message.created_at))}</span>
            </div>
          )}
        </div>

        {isUnread && <div className=" w-2 h-2 bg-brand rounded-full" />}
      </Link>

      {isLogged && (
        <ThreeDots
          className="cursor-pointer"
          fill="gray"
          onClick={(e) => {
            setIsOpenActions((p) => !p);
          }}
        />
      )}

      {isOpenActions && item?.id && (
        <ul className="absolute bg-secondary border border-primary rounded p-1 right-3 top-12 z-10">
          <li>
            <button
              disabled={isLoading}
              onClick={(e) => {
                e.stopPropagation();
                mutate(
                  { conversationId: item?.id, action: 'archive' },
                  {
                    onSuccess: () => {
                      onSuccess('Conversation archived.');
                      reactQuery.refetchQueries(['all-conversations']);
                    }
                  }
                );
              }}
              type="button"
              className="text-sm p-2 text-white text-opacity-30 hover:bg-primary hover:text-white w-full rounded-md text-left disabled:cursor-not-allowed"
            >
              Archive Conversation
            </button>
          </li>
          <li>
            <button
              disabled={isLoading}
              onClick={(e) => {
                e.stopPropagation();

                setIsOpenDeleteModal(true);
              }}
              type="button"
              className="text-sm p-2 text-white text-opacity-30 hover:bg-primary hover:text-white w-full rounded-md text-left disabled:cursor-not-allowed"
            >
              Delete Conversation
            </button>
          </li>
        </ul>
      )}

      {isOpenDeleteModal && item?.id && (
        <ConfirmDeleteModal
          disabledBtns={isLoading}
          message="Are you sure you want to delete this conversation?"
          onClose={() => setIsOpenDeleteModal(false)}
          onConfirm={() => {
            mutate(
              { conversationId: item?.id, action: 'delete' },
              {
                onSuccess: () => {
                  onSuccess('Conversation deleted.');
                  if (bot_id) removeChat(bot_id);

                  reactQuery.refetchQueries(['all-conversations']).then(() => {
                    mostRecentConversation
                      ? push(`/chat/${mostRecentConversation.account_id}/${mostRecentConversation.bot_name}`)
                      : push('/');
                  });
                }
              }
            );
          }}
        />
      )}
    </div>
  );
};
